import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { FormGroup, CheckBox, TextArea, FormSelect } from '~/components/form';
import { FileDrop } from '~/components';
import { useForm } from '~/hooks';
import { document, policySearchRequest } from '~/api';
const { DocumentType } = document;
import { attachFile, PolicySearchRequestStatus } from './common';
import ResultsGrid from './ResultsGrid';
import { DateTime } from 'luxon';
import {IoMdSave, IoMdThumbsUp} from 'react-icons/io';
import FormError from '~/components/form/form-error';

const uploadAttachments = async (requestId, values) => {
    const {
        ownersPolicy,
        previousOwnersPolicy,
        lendersPolicy,
        previousLendersPolicy
    } = values;

    if (Array.isArray(ownersPolicy) && ownersPolicy.length) {
        const type = previousOwnersPolicy
            ? DocumentType.PREVIOUS_OWNER_POLICY
            : DocumentType.CURRENT_OWNER_POLICY;

        await attachFile(requestId, type)(ownersPolicy[0]);
    }

    if (Array.isArray(lendersPolicy) && lendersPolicy.length) {
        const type = previousLendersPolicy
            ? DocumentType.PREVIOUS_LENDER_POLICY
            : DocumentType.CURRENT_LENDER_POLICY;

        await attachFile(requestId, type)(lendersPolicy[0]);
    }
};

const getFoundSystem = (values) => {
    const {
        selectedResult,
        internalSystems,
        foundSystem: maybeFound
    } = values;

    let foundSystem;

    if (selectedResult == null) {
        const foundExternal = typeof maybeFound == 'object' && Object.keys(maybeFound).length > 0; 

        if (foundExternal) {
            foundSystem = maybeFound;
        } else {
            throw 'No found system specified';
        }
    } else {
        const foundSystemId = selectedResult.systemId;
        foundSystem = internalSystems.find(s => s.value == foundSystemId);

        if (foundSystem == null) {
            throw 'Problem associating internal foundSystem';
        }
    }

    return foundSystem;
};

const onSubmit = async ({values, props, args, onFail, onInvalidForm}) => {

    const {
        previousUnderwriter,
        emailComments,
        notes,
        previousPolicyDate,
        selectedResult,
        externalSystems
    } = values;

    const { request, requestedBy_needsAccount } = props;

    let foundSystem = null;

    try {
        foundSystem = getFoundSystem(values);
    } catch (err) {
        if(!args.shouldSaveResultsOnsubmit && args.found) {
            return values.hasResults
                ? onInvalidForm({selectedResult: 'Please select a result above, or specify an external result'})
                : onInvalidForm({foundSystem: 'Which system was the policy found in?'});
        }
    }

    if (args.found) {
        const hasSearchedSystems = Array.isArray(values.searchedSystems) && values.searchedSystems.length >= 1;

        if (!hasSearchedSystems && externalSystems.includes(foundSystem)) {
            return onInvalidForm({ searchedSystems: 'Which external systems did you search?'});
        }
    }
    
    try {
        await uploadAttachments(request.id, values);

        let completed = DateTime.now().toJSDate();

        let statusId = args.found
            ? PolicySearchRequestStatus.Found 
            : PolicySearchRequestStatus.NotFound;

        if(args.shouldSaveResultsOnsubmit) {
            // Save PSR in Review status
            statusId = PolicySearchRequestStatus.Review;
            completed = null;
        }
                
        const searchedSystems = Array.isArray(values.searchedSystems)
            ? values.searchedSystems.map(s => s.value)
            : null;
        
        const update = {
            statusId,
            completed,
            searchedSystems,
            foundSystem,
            emailComments,
            notes,
            previousUnderwriter,
            selectedResult,
            previousPolicyDate,
            requestedBy_needsAccount
        };

        const updated = await policySearchRequest.completeSearch(request.id, update);

        return updated;
    } catch (err) {
        console.error(err);
        return onFail(err.message);
    }
};

const loadResults = (request, result, isComplete) => async (values) => {
    const { _data: requestData } = request;

    // const result = await policySearchRequest.getSearchResults(request.id);

    const previousUnderwriter = Object.hasOwnProperty.call(requestData, "previousUnderwriter") ? requestData?.previousUnderwriter : null;
    const previousPolicyDate = Object.hasOwnProperty.call(requestData, "previousPolicyDate") ? requestData?.previousPolicyDate : null;
    const selectedResult = Object.hasOwnProperty.call(requestData, "selectedResult") ? requestData?.selectedResult : null;

    const rawSearchSystems = await policySearchRequest.getExternalSystems();

    const allSearchSystems = rawSearchSystems
        .map(s => ({ label: s.name, value: s.id }));

    const internalSystems = rawSearchSystems
        .filter(r => r.internal)
        .map(s => ({ label: s.name, value: s.id }));

    const externalSystems = rawSearchSystems
        .filter(r => !r.internal)
        .map(s => ({ label: s.name, value: s.id }));

    let searchedSystems = values.searchedSystems;

    if (requestData.searchedSystems) {
        const searched = requestData.searchedSystems;
        if (Array.isArray(searched)) {
            searchedSystems = searched.map(s => externalSystems.find(e => e.value == s));
        }
    }

    let foundSystem = values.foundSystem;
    
    if (requestData.foundSystem) {
        if (typeof requestData.foundSystem == 'object') {
            foundSystem = requestData.foundSystem;
        } else {
            foundSystem = allSearchSystems.find(s => s.value == requestData.foundSystem);
        }
    }

    const found = isComplete
        ? request.statusId == PolicySearchRequestStatus.Found
        : values.found;

    let hasResults = result.results && result.results.length > 0;

    if (found) {
        const maybeInternal = internalSystems
            .find(i => i.value == foundSystem.value);

        if (maybeInternal == undefined)
            hasResults = false;
    }

    let _selectedResult = selectedResult;

    let ownersPolicy = values.ownersPolicy;
    let previousOwnersPolicy = false;
    let lendersPolicy = values.lendersPolicy;
    let previousLendersPolicy = false;


    if (Array.isArray(request.documents)) {
        const mappedDocs = request.documents.map(d => {
            return {
                file: { path: d.name },
                type: d.typeId,
                ...d
            };
        });

        const { 
            PREVIOUS_OWNER_POLICY, 
            CURRENT_OWNER_POLICY,
            PREVIOUS_LENDER_POLICY,
            CURRENT_LENDER_POLICY
        } = DocumentType;

        ownersPolicy = mappedDocs
            .filter(d => [PREVIOUS_OWNER_POLICY, CURRENT_OWNER_POLICY].includes(d.typeId));
        
        previousOwnersPolicy = ownersPolicy.length > 0
            && ownersPolicy[0].typeId == PREVIOUS_OWNER_POLICY;

        lendersPolicy = mappedDocs
            .filter(d => [PREVIOUS_LENDER_POLICY, CURRENT_LENDER_POLICY].includes(d.typeId));

        previousLendersPolicy = lendersPolicy.length > 0
            && lendersPolicy[0].typeId == PREVIOUS_LENDER_POLICY;
    }

    return {
        ...values,
        results: result.results,
        hasResults,
        foundSystem,
        found,
        previousUnderwriter,
        previousPolicyDate,
        selectedResult: _selectedResult,
        searchedSystems,
        allSearchSystems,
        internalSystems,
        externalSystems,
        previousLendersPolicy,
        lendersPolicy,
        previousOwnersPolicy,
        ownersPolicy,
        notes: requestData.completionNotes || values.notes,
        emailComments: requestData.emailComments || values.emailComments
    };
};

const SearchResults = ({ request, result, onComplete, isComplete = false, showSaveButton = false, requestedBy_needsAccount, setLoader }) => {

    const onLoad = useCallback(
        loadResults(request, result, isComplete),
        [request, result, isComplete]);

    const shouldSaveResultsOnsubmit = showSaveButton;

    const {
        state: { values, errors },
        fieldActions,
        formActions
    } = useForm({
        initialState: {
            searchedSystems: null,
            found: false,
            foundSystem: null,
            previousUnderwriter: '',
            ownersPolicy: null,
            previousOwnersPolicy: false,
            lendersPolicy: null,
            previousLendersPolicy: false,
            previousPolicyDate: null,
            emailComments: '',
            notes: '',
            selectedResult: null,
            results: null,
            hasResults: false
        },
        props: { request, result, isComplete, requestedBy_needsAccount, shouldSaveResultsOnsubmit },
        onSubmit,
        onLoad
    });
    
    const [showExtSystems, setShowExtSystems] = useState();
    useEffect(() => setShowExtSystems(!values.hasResults), [values.hasResults]);
    useEffect(() => {
        if (isComplete && values.found) {
            const foundExternal = values.externalSystems
                .find(e => {
                    return e.value == values.foundSystem.value;
                });

            setShowExtSystems(foundExternal != undefined);
        }
    }, [isComplete, values.found, values.foundSystem]);
    
    const gridResults = useMemo(() => {
        if (isComplete) {
            const maybeInternalSystem = Array.isArray(values.internalSystems)
                ? values.internalSystems.find(i => i.value == values.foundSystem.value)
                : null;

            return maybeInternalSystem == undefined 
                ? []
                : [ values.selectedResult ];

        } else {
            return values.results;
        }
    }, [values.results, values.selectedResult, values.internalSystems]);

    const onRowClick = (row, index) => {
        if (!isComplete) {
            formActions.setError({ selectedResult: null });
            if (values.selectedResult && index == values.selectedResult.index) {
                fieldActions.onChange(null, 'selectedResult');
            } else {
                fieldActions.onChange({ ...row, index }, 'selectedResult');
            }
        }
    };

    const initialResultSelection = useMemo(
        () => values.hasResults && values.selectedResult ? values.selectedResult.index : -1,
        [values.hasResults, isComplete]);

    return (
        <section className='mt-8 grid grid-cols-12 grid-flow-row gap-4 border-t-2 border-gray-300 pt-8'>
            {values.hasResults && (
                <>
                    <div className="col-span-12 flex py-2 items-center justify-between">
                        <h3 className="text-xl font-bold text-blue-60">Internal Search Results</h3>
                        {!isComplete && (
                            <button onClick={() => setShowExtSystems(!showExtSystems)} className=" text-lg font-semibold underline text-yellow-500">
                                Found in Another System
                            </button>
                        )}
                    </div>
                    <ResultsGrid 
                        results={gridResults} 
                        events={{ onRowClick }}
                        isComplete={isComplete}
                        initialState={{ selectedRow: initialResultSelection }}
                        className="col-span-12 mb-2 overflow-y-auto max-h-80"/>
                    <FormError value={errors.selectedResult} className="col-span-12 mb-2 -mt-2"/>
                </>
            )}
            {!values.hasResults && (
                <p className="col-span-12 text-yellow-500 font-semibold text-xl mb-8">
                    No results found internally. {!isComplete && ('Please check other systems.')}
                </p>
            )}
            {showExtSystems && (
                <>            
                    <FormSelect
                        label="Systems Searched"
                        required
                        readOnly={isComplete}
                        isSearchable
                        isMulti
                        options={values.externalSystems}
                        value={values.searchedSystems}
                        error={errors.searchedSystems}
                        groupClassName="col-span-6"
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        path='searchedSystems'
                        {...fieldActions}
                    />
                    <FormSelect
                        label="System Found In"
                        readOnly={isComplete}
                        isSearchable
                        isClearable
                        options={values.externalSystems}
                        value={values.foundSystem}
                        error={errors.foundSystem}
                        groupClassName="col-span-6"
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        path='foundSystem'
                        {...fieldActions}
                    />
                </>
            )}
            <FormGroup
                label="Previous Underwriter"
                readOnly={isComplete}
                groupClassName="col-span-6 col-start-1"
                inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                path='previousUnderwriter'
                value={values.previousUnderwriter}
                error={errors.previousUnderwriter}
                {...fieldActions} 
            />  
            {showExtSystems && (
                <FormGroup
                    label="Previous Policy Date"
                    type='date-picker'
                    readOnly={isComplete}
                    name='previousPolicyDate'
                    dateFormat="MM/dd/yyyy"
                    groupClassName="col-span-6"
                    inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                    datePickerClasses="w-full"
                    path='previousPolicyDate'
                    value={values.previousPolicyDate}
                    error={errors.previousPolicyDate}
                    {...fieldActions}
                />
            )}          
            <FileDrop
                heading="Upload Owner's Policy"
                className='h-12'
                readOnly={isComplete}
                groupClassName="col-span-4 col-start-1"
                files={values.ownersPolicy}
                onChange={files => fieldActions.onChange(files, 'ownersPolicy')}
                onRemove={() => fieldActions.onChange([], 'ownersPolicy')} 
            />
            <CheckBox
                label="Previous Policy"
                readOnly={isComplete}
                path="previousOwnersPolicy"
                groupClassName="col-span-2 pt-4 flex items-center text-xs lg:text-sm align-center"
                inputClassName="border-1 border-gray-200 text-xs lg:text-sm rounded bg-white mr-2"
                value={values.previousOwnersPolicy}
                {...fieldActions}
            />
            <FileDrop
                heading="Upload Lender's Policy"
                className='h-12'
                readOnly={isComplete}
                groupClassName="col-span-4"
                files={values.lendersPolicy}
                onChange={files => fieldActions.onChange(files, 'lendersPolicy')}
                onRemove={() => fieldActions.onChange([], 'lendersPolicy')} 
            />
            <CheckBox
                label="Previous Policy"
                readOnly={isComplete}
                path="previousLendersPolicy"
                groupClassName="col-span-2 pt-4 flex items-center text-xs lg:text-sm align-center justify-content-center"
                inputClassName="border-1 border-gray-200 text-xs lg:text-sm rounded bg-white mr-2"
                value={values.previousLendersPolicy}
                {...fieldActions}
            />
            <TextArea
                type='textarea'
                readOnly={isComplete}
                label='Email Comments'
                name='emailComments'
                path='emailComments'
                value={values.emailComments}
                {...fieldActions}
                groupClassName='col-span-12'
                inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50' 
            />
            <TextArea
                type='textarea'
                readOnly={isComplete}
                label='Internal Notes'
                name='notes'
                path='notes'
                value={values.notes}
                {...fieldActions}
                groupClassName='col-span-12'
                inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50' 
            />
            {!isComplete && (
                <div className="col-span-12 flex items-center justify-end h-16">
                    {showSaveButton ?
                        <button
                            className="rounded-full py-2 px-4 bg-lightGreen-300 font-semibold text-sm text-white ml-4 flex items-center focus:outline-none"
                            onClick={() => {
                                setLoader(true);

                                formActions.submit((res) => {
                                    if (res) {
                                        window.location = '/policySearch';
                                    } else {
                                        setLoader(false);
                                    }
                                }, { shouldSaveResultsOnsubmit: true });
                            }}>
                            Save and Start New
                            <IoMdSave className="ml-2 text-white" size='1.125rem'/>
                        </button>
                        :
                        <>
                            <button className="rounded-full py-2 px-4 bg-gray-100 font-semibold text-sm focus:outline-none"
                                onClick={() => {
                                    setLoader(true);
                                    formActions.submit((res) => {
                                        if (res) {
                                            onComplete();
                                        } else {
                                            setLoader(false);
                                        }
                                    }, { found: false });
                                }}>
                        Not Found
                            </button>
                            <button 
                                className="rounded-full py-2 px-4 bg-lightGreen-300 font-semibold text-sm text-white ml-4 flex items-center focus:outline-none"
                                onClick={() => {
                                    setLoader(true);

                                    formActions.submit((res) => {
                                        if (res) {
                                            onComplete();
                                        } else {
                                            setLoader(false);
                                        }
                                    }, { found: true });
                                }}>
                        Found
                                <IoMdThumbsUp className="ml-2 text-white" size='1.125rem'/>
                            </button>
                        </>
                    }
                </div>
            )}
        </section>
    );
};

export default SearchResults;